<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useToastStore } from '@/stores/toast'
import { useAuthStore } from '@/stores/auth'
import { usePersonnelStore } from '@/stores/personnel'
import { useOpsStore } from '@/stores/ops'
import { useRosterStore } from '@/stores/roster'
import {
  SearcherType,
  RopeQual,
  OverheadRole,
  SEARCHER_TYPE_OPTIONS,
  ROPE_QUAL_OPTIONS,
  STANDARD_MISC_QUALS,
  MEDICAL_QUAL_OPTIONS,
  OVERHEAD_ROLE_OPTIONS,
  type RosterMember,
} from '@/types'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosChip from '@/components/ui/FosChip.vue'

const props = defineProps<{
  incidentId: string
  // When opened from an email invite, preselect the overhead join path + role.
  prefillOverheadRole?: OverheadRole | null
}>()
const visible = defineModel<boolean>('visible', { default: false })

const toast = useToastStore()
const authStore = useAuthStore()
const personnelStore = usePersonnelStore()
const opsStore = useOpsStore()
const rosterStore = useRosterStore()

const last = ref('')
const first = ref('')
const agency = ref('')
const phone = ref('')
const notes = ref('')
const medicalQual = ref('')
const searcherType = ref<SearcherType | null>(null)
const ropeQual = ref<RopeQual | null>(null)
const miscQuals = ref<string[]>([])

const step = ref<1 | 2>(1)
const joinAs = ref<'field' | 'overhead' | null>(null)
const selectedOverheadRole = ref<OverheadRole | null>(null)
const icTakeoverConfirmed = ref(false)
const submitting = ref(false)
const isSelfCheckIn = ref(false)
// Agency roster (D4H) lookup — IC checking someone else in
const rosterQuery = ref('')
const pickedRosterMember = ref<RosterMember | null>(null)
const rosterResults = computed(() =>
  pickedRosterMember.value ? [] : rosterStore.search(rosterQuery.value),
)

const currentIC = computed(() =>
  personnelStore.roster.find((p) => p.category === OverheadRole.IC && p.time_out === null),
)

const showICTakeover = computed(
  () => selectedOverheadRole.value === OverheadRole.IC && !!currentIC.value,
)

const canContinue = computed(
  () => last.value.trim() !== '' && first.value.trim() !== '' && agency.value.trim() !== '',
)

const canSubmit = computed(() => {
  if (!joinAs.value) return false
  if (joinAs.value === 'overhead' && !selectedOverheadRole.value) return false
  if (showICTakeover.value && !icTakeoverConfirmed.value) return false
  return !submitting.value
})

const alreadyCheckedIn = computed(() =>
  authStore.currentUser
    ? personnelStore.roster.some(
        (p) => p.user_id === authStore.currentUser!.uid && p.time_out === null,
      )
    : false,
)

function resetForm() {
  last.value = ''
  first.value = ''
  agency.value = ''
  phone.value = ''
  notes.value = ''
  medicalQual.value = ''
  searcherType.value = null
  ropeQual.value = null
  miscQuals.value = []
  step.value = 1
  joinAs.value = null
  selectedOverheadRole.value = null
  icTakeoverConfirmed.value = false
  isSelfCheckIn.value = false
  rosterQuery.value = ''
  pickedRosterMember.value = null
}

async function loadAgencyName(): Promise<string> {
  const agencyId = authStore.userProfile?.agency_id
  if (!agencyId) return ''
  const snap = await getDoc(doc(db, 'agencies', agencyId))
  return snap.exists() ? (snap.data() as { agency_name: string }).agency_name : ''
}

async function pickRosterMember(member: RosterMember) {
  pickedRosterMember.value = member
  isSelfCheckIn.value = false
  rosterQuery.value = `${member.first} ${member.last}`
  last.value = member.last
  first.value = member.first
  phone.value = member.phone
  medicalQual.value = member.medical_qual ?? ''
  searcherType.value = member.searcher_type
  ropeQual.value = member.rope_qual
  miscQuals.value = [...member.misc_quals]
  agency.value = await loadAgencyName()
}

function clearRosterPick() {
  pickedRosterMember.value = null
  rosterQuery.value = ''
}

async function prefillFromProfile() {
  if (!authStore.userProfile || alreadyCheckedIn.value) return
  last.value = authStore.userProfile.user_last_name
  first.value = authStore.userProfile.user_first_name
  agency.value = await loadAgencyName()
  phone.value = authStore.userProfile.phone ?? ''
  medicalQual.value = authStore.userProfile.medical_qual ?? ''
  searcherType.value = authStore.userProfile.searcher_type
  ropeQual.value = authStore.userProfile.rope_qual
  miscQuals.value = [...(authStore.userProfile.misc_quals ?? [])]
  isSelfCheckIn.value = true
}

watch([last, first], () => {
  if (
    isSelfCheckIn.value &&
    (last.value.trim() !== (authStore.userProfile?.user_last_name ?? '') ||
      first.value.trim() !== (authStore.userProfile?.user_first_name ?? ''))
  ) {
    isSelfCheckIn.value = false
  }
  if (
    pickedRosterMember.value &&
    (last.value.trim() !== pickedRosterMember.value.last ||
      first.value.trim() !== pickedRosterMember.value.first)
  ) {
    pickedRosterMember.value = null
  }
})

watch(visible, async (val) => {
  if (val) {
    resetForm()
    if (authStore.userProfile?.agency_id) rosterStore.subscribe(authStore.userProfile.agency_id)
    await prefillFromProfile()
    if (props.prefillOverheadRole) {
      joinAs.value = 'overhead'
      selectedOverheadRole.value = props.prefillOverheadRole
    }
  }
})

function goToStep2() {
  if (!canContinue.value) return
  step.value = 2
}

async function handleSubmit() {
  if (!canSubmit.value) return
  submitting.value = true
  try {
    await personnelStore.checkIn(
      props.incidentId,
      opsStore.selectedOpId!,
      {
        user_id: isSelfCheckIn.value
          ? (authStore.currentUser?.uid ?? null)
          : (pickedRosterMember.value?.user_id ?? null),
        last: last.value.trim(),
        first: first.value.trim(),
        agency: agency.value.trim(),
        phone: phone.value.trim(),
        when_made: new Date().toISOString(),
        resource: '',
        qualifications: {
          medical_qual: medicalQual.value,
          searcher_type: searcherType.value,
          rope_qual: ropeQual.value,
          misc_quals: miscQuals.value,
        },
        category: '',
        team_id: null,
        notes: notes.value.trim(),
      },
      joinAs.value!,
      selectedOverheadRole.value ?? undefined,
    )
    toast.add(`${first.value} ${last.value} added to roster`, 'success', 3000)
    visible.value = false
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Check-in failed'
    toast.add(msg, 'error', 4000)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <FosDialog
    :open="visible"
    :title="`Personnel Check-In — Step ${step} of 2`"
    max-width="max-w-xl"
    @update:open="visible = $event"
  >
    <!-- Step 1: Details -->
    <div v-if="step === 1" class="flex flex-col gap-5">
      <!-- Agency roster lookup (hidden when the agency has no roster) -->
      <div v-if="rosterStore.members.length > 0" class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">Find in Agency Roster</label>
        <div class="relative">
          <FosInput
            v-model="rosterQuery"
            placeholder="Search by name or email"
            :disabled="!!pickedRosterMember"
          />
          <div
            v-if="rosterResults.length > 0"
            class="absolute z-10 left-0 right-0 mt-1 bg-surface border border-border rounded overflow-hidden"
          >
            <button
              v-for="m in rosterResults"
              :key="m.roster_id"
              type="button"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 text-left border-b border-border last:border-0 hover:bg-surface-container"
              @click="pickRosterMember(m)"
            >
              <span class="text-sm text-on-surface">{{ m.last }}, {{ m.first }}</span>
              <FosChip v-if="!m.operational" variant="warning">Non-Op</FosChip>
            </button>
          </div>
        </div>
        <div v-if="pickedRosterMember" class="flex items-center gap-2">
          <FosChip v-if="!pickedRosterMember.operational" variant="warning">Non-operational in D4H</FosChip>
          <FosButton variant="tertiary" size="sm" @click="clearRosterPick">Clear</FosButton>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest">Last Name <span class="text-error">*</span></label>
          <FosInput v-model="last" placeholder="Last name" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest">First Name <span class="text-error">*</span></label>
          <FosInput v-model="first" placeholder="First name" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest">Agency <span class="text-error">*</span></label>
          <FosInput v-model="agency" placeholder="Agency name" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest">Phone</label>
          <FosInput v-model="phone" placeholder="(555) 000-0000" type="tel" />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">Notes</label>
        <FosInput v-model="notes" placeholder="Any additional notes" />
      </div>

      <div class="h-px bg-surface-container-low" />

      <!-- Qualifications -->
      <div class="flex flex-col gap-4">
        <span class="text-[10px] text-outline font-label uppercase tracking-widest">Qualifications</span>

        <!-- Medical -->
        <div class="flex flex-col gap-2">
          <label class="text-[10px] text-outline font-mono uppercase tracking-widest">Medical</label>
          <div class="flex flex-wrap gap-x-5 gap-y-2">
            <label
              v-for="opt in MEDICAL_QUAL_OPTIONS"
              :key="opt"
              class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant font-mono"
            >
              <input type="radio" :value="opt" v-model="medicalQual" class="accent-primary" />
              {{ opt }}
            </label>
          </div>
        </div>

        <!-- Searcher Type -->
        <div class="flex flex-col gap-2">
          <label class="text-[10px] text-outline font-mono uppercase tracking-widest">Searcher Type</label>
          <div class="flex flex-wrap gap-x-5 gap-y-2">
            <label
              v-for="opt in SEARCHER_TYPE_OPTIONS"
              :key="opt"
              class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant font-mono"
            >
              <input type="radio" :value="opt" v-model="searcherType" class="accent-primary" />
              {{ opt }}
            </label>
          </div>
        </div>

        <!-- Rope -->
        <div class="flex flex-col gap-2">
          <label class="text-[10px] text-outline font-mono uppercase tracking-widest">Rope</label>
          <div class="flex flex-wrap gap-x-5 gap-y-2">
            <label
              v-for="opt in ROPE_QUAL_OPTIONS"
              :key="opt"
              class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant font-mono"
            >
              <input type="radio" :value="opt" v-model="ropeQual" class="accent-primary" />
              {{ opt }}
            </label>
          </div>
        </div>

        <!-- Other -->
        <div class="flex flex-col gap-2">
          <label class="text-[10px] text-outline font-mono uppercase tracking-widest">Other</label>
          <div class="grid grid-cols-2 gap-x-5 gap-y-2">
            <label
              v-for="opt in STANDARD_MISC_QUALS"
              :key="opt"
              class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant font-mono"
            >
              <input type="checkbox" :value="opt" v-model="miscQuals" class="accent-primary" />
              {{ opt }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: Role Selection -->
    <div v-else class="flex flex-col gap-5">
      <p class="text-[10px] text-outline font-label uppercase tracking-widest">How are you joining this operation?</p>

      <div class="flex flex-col gap-2">
        <!-- Overhead option -->
        <button
          type="button"
          class="flex items-start gap-4 p-4 text-left transition-colors"
          :class="joinAs === 'overhead' ? 'bg-primary-container/20 outline outline-1 outline-primary' : 'bg-surface-container-low hover:bg-surface-container'"
          @click="joinAs = 'overhead'"
        >
          <div
            class="w-4 h-4 border-2 flex-shrink-0 mt-0.5 flex items-center justify-center"
            :class="joinAs === 'overhead' ? 'border-primary' : 'border-outline'"
          >
            <div v-if="joinAs === 'overhead'" class="w-2 h-2 bg-primary" />
          </div>
          <div>
            <span class="font-mono text-sm text-on-surface font-bold block uppercase tracking-widest">Overhead</span>
            <span class="text-xs text-on-surface-variant font-mono">IC, Ops, Plans, Logistics, etc.</span>
          </div>
        </button>

        <!-- Field option -->
        <button
          type="button"
          class="flex items-start gap-4 p-4 text-left transition-colors"
          :class="joinAs === 'field' ? 'bg-primary-container/20 outline outline-1 outline-primary' : 'bg-surface-container-low hover:bg-surface-container'"
          @click="joinAs = 'field'; selectedOverheadRole = null"
        >
          <div
            class="w-4 h-4 border-2 flex-shrink-0 mt-0.5 flex items-center justify-center"
            :class="joinAs === 'field' ? 'border-primary' : 'border-outline'"
          >
            <div v-if="joinAs === 'field'" class="w-2 h-2 bg-primary" />
          </div>
          <div>
            <span class="font-mono text-sm text-on-surface font-bold block uppercase tracking-widest">Field Unit</span>
            <span class="text-xs text-on-surface-variant font-mono">Assigned to a search team in the field</span>
          </div>
        </button>
      </div>

      <!-- Overhead role dropdown -->
      <div v-if="joinAs === 'overhead'" class="flex flex-col gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest">
            Overhead Role <span class="text-error">*</span>
          </label>
          <FosSelect
            :model-value="selectedOverheadRole ?? undefined"
            :options="OVERHEAD_ROLE_OPTIONS"
            placeholder="Select a role"
            @update:model-value="selectedOverheadRole = $event as OverheadRole; icTakeoverConfirmed = false"
          />
        </div>

        <!-- IC takeover confirmation -->
        <div v-if="showICTakeover" class="bg-surface-container-high p-4 flex flex-col gap-3">
          <div class="flex gap-3">
            <span class="material-symbols-outlined text-[18px] text-error mt-0.5 flex-shrink-0">warning</span>
            <div>
              <p class="text-sm font-mono font-bold text-on-surface">
                {{ currentIC!.first }} {{ currentIC!.last }} is already IC.
              </p>
              <p class="text-xs text-on-surface-variant font-mono mt-0.5">Are you taking command?</p>
            </div>
          </div>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="icTakeoverConfirmed" class="accent-primary" />
            <span class="text-sm font-mono text-on-surface">Yes, I am taking command</span>
          </label>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between gap-2 w-full">
        <FosButton v-if="step === 2" variant="secondary" @click="step = 1">
          <span class="material-symbols-outlined text-[14px]">arrow_back</span>
          Back
        </FosButton>
        <div v-else />
        <div class="flex gap-2">
          <FosButton variant="tertiary" @click="visible = false">Cancel</FosButton>
          <FosButton v-if="step === 1" variant="primary" :disabled="!canContinue" @click="goToStep2">
            Continue
            <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
          </FosButton>
          <FosButton v-else variant="primary" :disabled="!canSubmit" @click="handleSubmit">
            <span v-if="submitting" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white animate-spin" />
            Confirm Check-In
          </FosButton>
        </div>
      </div>
    </template>
  </FosDialog>
</template>
